import { useCallback, useMemo, useState } from 'react';
import { calculateDealPrice } from '../utils/pricing/dealPricing';
import { calculateCartGctBreakdown } from '../utils/pricing/gctTax';
import { resolveWipayCountryCode } from '../services/payment/wipayPlatform';

/**
 * Checkout pricing + payment-type / gateway env config.
 *
 * NOTE / BUG: Missing REACT_APP_CONFIRMATION_FEE defaults to 0 here (blocks checkout),
 * while WipayResponsePage / finalizeCartPayment default to 500. Keep env set in all
 * environments; do not "fix" by changing the default without aligning those callers.
 */
export function useCheckoutPricing({
  cartInstance,
  inventoryDetails,
  supermarket,
  deliveryOption,
}) {
  const [paymentType, setPaymentType] = useState('confirmation_fee_only');

  // BUG: default 0 vs 500 elsewhere — see module note above.
  const confirmationFee = parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 0;
  const gatewayPaymentType = process.env.REACT_APP_PAYMENT_TYPE || 'wipay';
  const wipayResponseUrl =
    process.env.REACT_APP_WIPAY_RESPONSE_URL || 'http://localhost:3000/payment/response';
  const wipayAccountNumber = process.env.REACT_APP_WIPAY_ACCOUNT_NUMBER || '1234567890';
  const wipayEnvironment = process.env.REACT_APP_WIPAY_ENVIRONMENT || 'sandbox';
  const wipayFeeStructure = process.env.REACT_APP_WIPAY_FEE_STRUCTURE || 'merchant_absorb';
  const wipayCountryCode = resolveWipayCountryCode();
  const wipayCardType = process.env.REACT_APP_WIPAY_CARD_TYPE || 'visa';
  const simulatedPaymentFails = process.env.REACT_APP_SIMULATED_PAYMENT_FAILS === 'true';

  const calculateDealPriceForProduct = useCallback(
    (productId, quantity, regularPrice) =>
      calculateDealPrice({
        quantity,
        regularPrice,
        inventoryData: inventoryDetails[productId],
      }),
    [inventoryDetails]
  );

  const orderSubtotal = useMemo(() => {
    if (!cartInstance?.products) return 0;
    return cartInstance.products.reduce((sum, p) => {
      const dealCalculation = calculateDealPriceForProduct(
        p.productId,
        p.quantity,
        p.price || 0
      );
      return sum + dealCalculation.price;
    }, 0);
  }, [cartInstance, calculateDealPriceForProduct]);

  // BUG: delivery fee is client-sourced from supermarket doc with no geocode /
  // distance validation; the value is written into checkoutSnapshot / WiPay total.
  const applicableDeliveryFee = useMemo(() => {
    if (deliveryOption !== 'delivery' || !supermarket) return 0;
    const fee = parseFloat(supermarket.delivery_fee);
    return !Number.isNaN(fee) && fee > 0 ? fee : 0;
  }, [deliveryOption, supermarket]);

  const gctSummary = useMemo(
    () =>
      calculateCartGctBreakdown({
        cartItems: cartInstance?.products || [],
        inventoryDetails,
        supermarket: supermarket || {},
        getLineTotal: (product) =>
          calculateDealPriceForProduct(product.productId, product.quantity, product.price || 0)
            .price,
      }),
    [cartInstance, inventoryDetails, supermarket, calculateDealPriceForProduct]
  );

  const hasConfiguredDeliveryFee =
    supermarket?.delivery_fee != null && parseFloat(supermarket.delivery_fee) > 0;
  const configuredMaxDeliveryDistance =
    supermarket?.max_delivery_distance != null &&
    parseFloat(supermarket.max_delivery_distance) > 0
      ? parseFloat(supermarket.max_delivery_distance)
      : null;

  const fullPaymentTotal = orderSubtotal + confirmationFee + applicableDeliveryFee;
  const confirmationPaymentTotal = confirmationFee + applicableDeliveryFee;
  const dueLaterLabel = deliveryOption === 'delivery' ? 'on delivery' : 'at pickup';

  return {
    paymentType,
    setPaymentType,
    confirmationFee,
    gatewayPaymentType,
    wipayResponseUrl,
    wipayAccountNumber,
    wipayEnvironment,
    wipayFeeStructure,
    wipayCountryCode,
    wipayCardType,
    simulatedPaymentFails,
    calculateDealPriceForProduct,
    orderSubtotal,
    applicableDeliveryFee,
    gctSummary,
    hasConfiguredDeliveryFee,
    configuredMaxDeliveryDistance,
    fullPaymentTotal,
    confirmationPaymentTotal,
    dueLaterLabel,
  };
}
